import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type ProcedureStepComment, type Stored } from "@overmap-ai/common"
import { selectCurrentUser } from "@overmap-ai/core"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"

interface ProcedureStepCommentDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	trigger: ReactElement
	procedureStepComment: Stored<ProcedureStepComment>
	onResolvedChange: (procedureStepComment: Stored<ProcedureStepComment>, resolved: boolean) => void
	onEdit: () => void
	onDelete: (procedureStepComment: Stored<ProcedureStepComment>) => void
}

export const ProcedureStepCommentDropdownMenu = memo((props: ProcedureStepCommentDropdownMenuProps) => {
	const {
		trigger,
		disabled,
		procedureStepComment,
		onResolvedChange,
		onEdit,
		onDelete,
		size,
		radius,
		variant,
		accentColor,
	} = props
	const currentUser = useAppSelector(selectCurrentUser)

	const handleResolvedChange = useCallback(() => {
		onResolvedChange(procedureStepComment, !procedureStepComment.resolved)
	}, [onResolvedChange, procedureStepComment])

	const handleDelete = useCallback(() => {
		onDelete(procedureStepComment)
	}, [onDelete, procedureStepComment])

	return (
		<Menu.Root>
			<Menu.ClickTrigger disabled={disabled}>{trigger}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Group>
					<Menu.Item onSelect={handleResolvedChange}>
						<LuIcon
							icon={procedureStepComment.resolved ? modelIconMapping.refresh : modelIconMapping.check}
						/>
						{procedureStepComment.resolved ? "Unresolve" : "Resolve"}
					</Menu.Item>
					{currentUser?.id === procedureStepComment.created_by && (
						<>
							<Menu.Item onSelect={onEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</>
					)}
				</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureStepCommentDropdownMenu.displayName = "ProcedureStepCommentDropdownMenu"
